import { Draggable } from 'react-beautiful-dnd';
import { Controller, UseFieldArrayRemove, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton } from '@mui/material';

import DeleteIcon from '~/assets/icons/svgs/trash-bin.svg?react';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { VideoQRSchema } from '~/pages/CreateQR/sub-components/videoTemplate/schema';
import useVideoTemplateStore from '~/store/useVideoTemplateStore';
import { Video } from '~/types/video';
import { isMaxLength } from '~/utils/common';
import { convertYouTubeToEmbed } from '~/utils/video';

import { useStyles } from './VideoItem.styled';

type VideoItemProps = {
  index: number;
  item: Video;
  videoQrId?: number;
  dragTrigger: any;
  stopTrigger: any;
  remove: UseFieldArrayRemove;
};
export const VideoItem = ({ index, item, remove }: VideoItemProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<VideoQRSchema>();
  const styles = useStyles();

  const { addVideoIdToDelete } = useVideoTemplateStore();

  const handleRemoveItem = () => {
    remove(index);
    if (item.id) {
      addVideoIdToDelete(item.id);
    }
  };

  return (
    <Draggable key={item?.id} draggableId={String(item?.id)} index={index}>
      {(provided) => (
        <Box ref={provided.innerRef} {...provided.draggableProps} sx={styles.wrapper}>
          <Box sx={styles.header}>
            <Box sx={styles.headerLeft}>
              {item.sourceType === 'YOUTUBE' ? (
                <iframe
                  src={convertYouTubeToEmbed(item.sourceUrl)}
                  width="42"
                  height="42"
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video src={item.videoFile} poster={item.thumbnail ?? undefined} controls={false} />
              )}

              <Text variant="t16Regular" sx={{ whiteSpace: 'nowrap' }}>
                {`${t('builder:video.video.title')} ${index + 1}`}
              </Text>
            </Box>
            <Box sx={styles.icons}>
              <IconButton disableRipple {...provided.dragHandleProps}>
                <MenuIcon />
              </IconButton>
              <IconButton disableRipple onClick={handleRemoveItem}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>

          <Controller
            control={control}
            name={`basicInfo.video.files.${index}.description`}
            render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => {
              const maxLengthError = isMaxLength(value, 500); // Shouldn't be red

              return (
                <TextInput
                  sx={{ marginTop: '16px' }}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  fullWidth
                  placeholder={t('common:enterDescription')}
                  error={!!error?.message}
                  helperText={error?.message || maxLengthError}
                  inputProps={{ maxLength: 500 }}
                />
              );
            }}
          />
        </Box>
      )}
    </Draggable>
  );
};
